import { useState, useEffect, useRef, useMemo } from 'react';
import { fetchNVDBWasteContainers } from '../services/nvdbService';
import { fetchOSMWastePoints } from '../services/overpassService';
import { WastePoint, Coordinates, MapBounds } from '../types';
import { boundsOverlapRatio } from '../utils/boundsUtils';

export const useWasteStations = (
  active: boolean,
  zoom: number,
  currentMapCenter: Coordinates,
  currentViewBounds: MapBounds | null,
) => {
  const [allPoints, setAllPoints] = useState<WastePoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const lastFetchedBoundsRef = useRef<MapBounds | null>(null);

  useEffect(() => {
    if (!active || zoom < 9 || !currentViewBounds) {
      setIsLoading(false);
      if (!active) {
        setError(false);
        lastFetchedBoundsRef.current = null;
        setAllPoints([]);
      }
      return;
    }

    if (lastFetchedBoundsRef.current &&
        boundsOverlapRatio(lastFetchedBoundsRef.current, currentViewBounds) > 0.7) {
      return;
    }

    const fetchAll = async () => {
      setIsLoading(true);
      try {
        const [nvdbPoints, osmPoints] = await Promise.all([
          fetchNVDBWasteContainers(currentViewBounds),
          fetchOSMWastePoints(currentViewBounds),
        ]);
        setAllPoints(deduplicateWastePoints([...nvdbPoints, ...osmPoints]));
        setError(false);
        lastFetchedBoundsRef.current = currentViewBounds;
      } catch (e) {
        console.error("Failed to fetch waste stations:", e);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(fetchAll, 1500);
    return () => clearTimeout(timer);
  }, [currentViewBounds, active, zoom]);

  // waste_basket only visible at zoom >= 14
  const wastePoints = useMemo(() =>
    allPoints.filter(wp => wp.type !== 'waste_basket' || zoom >= 14),
    [allPoints, zoom]
  );

  return { wastePoints, isLoading, error };
};

/** Deduplicate points within ~30m, preferring NVDB source */
function deduplicateWastePoints(points: WastePoint[]): WastePoint[] {
  const THRESHOLD = 0.0003; // ~30m
  const result: WastePoint[] = [];
  const used = new Set<number>();

  // NVDB first so they get priority
  points.sort((a, b) => (a.source === 'nvdb' ? -1 : b.source === 'nvdb' ? 1 : 0));

  for (let i = 0; i < points.length; i++) {
    if (used.has(i)) continue;
    result.push(points[i]);
    used.add(i);

    // Mark near-duplicate OSM points if an NVDB point was kept
    if (points[i].source === 'nvdb') {
      for (let j = i + 1; j < points.length; j++) {
        if (used.has(j) || points[j].source === 'nvdb') continue;
        const latDiff = Math.abs(points[i].location.latitude - points[j].location.latitude);
        const lonDiff = Math.abs(points[i].location.longitude - points[j].location.longitude);
        if (latDiff < THRESHOLD && lonDiff < THRESHOLD) {
          used.add(j);
        }
      }
    }
  }
  return result;
}
